import React, { useState } from "react";
import { Modal, DatePicker, Typography, message } from "antd";
import { EditOutlined, FieldTimeOutlined } from "@ant-design/icons";
import { UiButton } from "../../ui";
import ModeCards from "../common/ModeCards";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import { getPharmacyConfigListApi } from "../../../services/pharmacyConfigs.services";
import { getPharmacyPurchaseReturnSuggestionsApi } from "../../../services/pharmacyPurchaseReturns.services";
import { getErrorMessage } from "../../../helpers/error";

const fetchVendors = (text) =>
  getPharmacyConfigListApi(
    "vendors",
    new URLSearchParams({ search: text || "", limit: 50 })
  ).then((res) =>
    (res?.data?.results || []).map((item) => ({ value: item.id, label: item.name }))
  );

const MODES = [
  {
    key: "manual",
    title: "Manual",
    hint: "Start with a blank return and pick the batches yourself.",
    icon: <EditOutlined />,
    color: "#3b6ef6",
    background: "#eaf0ff",
  },
  {
    key: "near_expiry",
    title: "Near Expiry",
    hint: "Pull in this vendor's batches expiring by a chosen date.",
    icon: <FieldTimeOutlined />,
    color: "#b42318",
    background: "#fef3f2",
  },
];

const first = (...values) => values.find((v) => v !== undefined && v !== null);

/*
 * A return line is keyed on the batch, not the medicine, so a suggestion is
 * only usable if a batch id comes back with it. The payload is read leniently:
 * the batch may arrive flattened or nested, and the quantity and rate under
 * any of several names.
 */
let rowSeq = 0;
const toRow = (item) => {
  const batch = typeof item?.batch === "object" && item.batch ? item.batch : item;
  const batchId = first(
    typeof item?.batch === "object" ? item.batch?.id : item?.batch,
    item?.batch_id,
    item?.id
  );
  const availableQty =
    Number(
      first(
        item?.available_qty,
        batch?.available_qty,
        item?.qty,
        item?.quantity,
        item?.return_qty,
        item?.stock_qty
      )
    ) || null;

  return {
    key: `pr-suggest-${(rowSeq += 1)}`,
    medicine: first(
      item?.medicine,
      item?.medicine_id,
      item?.pharmacy_medicine,
      batch?.medicine
    ),
    medicine_name:
      first(
        item?.medicine_name,
        item?.pharmacy_medicine_name,
        batch?.medicine_name,
        item?.name,
        item?.product_name
      ) || "",
    batch: batchId,
    // Seeds the batch dropdown so the line reads as a batch rather than an id.
    batches: batchId
      ? [
          {
            id: batchId,
            batch_number:
              first(batch?.batch_number, batch?.batch_no, item?.batch_number) ||
              "-",
            available_qty: availableQty ?? 0,
            expiry_date: first(batch?.expiry_date, batch?.expiry, item?.expiry_date),
          },
        ]
      : [],
    loadingBatches: false,
    qty: availableQty,
    rate:
      Number(
        first(
          item?.rate,
          batch?.rate,
          item?.purchase_rate,
          batch?.purchase_rate,
          item?.last_purchase_rate,
          item?.purchase_price
        )
      ) || null,
    gst_percent:
      Number(
        first(item?.gst_percent, item?.gst_percentage, item?.gst, item?.tax_percent)
      ) || 0,
  };
};

const readSuggestions = (data) => {
  const list = Array.isArray(data)
    ? data
    : data?.results || data?.items || data?.suggestions || data?.data || [];
  return (Array.isArray(list) ? list : []).map(toRow).filter((row) => row.batch);
};

const RaisePurchaseReturnModal = ({ open, onClose, onStart }) => {
  const [mode, setMode] = useState("manual");
  const [vendor, setVendor] = useState(undefined);
  const [vendorName, setVendorName] = useState("");
  const [expiresBy, setExpiresBy] = useState(null);
  const [loading, setLoading] = useState(false);

  const close = () => {
    setMode("manual");
    setVendor(undefined);
    setVendorName("");
    setExpiresBy(null);
    setLoading(false);
    onClose();
  };

  const handleContinue = () => {
    if (mode === "manual") {
      onStart(null);
      close();
      return;
    }

    if (!vendor) {
      message.warning("Select a vendor first.");
      return;
    }
    if (!expiresBy) {
      message.warning("Select an expiry cut-off date.");
      return;
    }

    setLoading(true);
    getPharmacyPurchaseReturnSuggestionsApi(
      new URLSearchParams({
        vendor,
        expires_by: expiresBy.format("YYYY-MM-DD"),
      })
    )
      .then((res) => {
        const rows = readSuggestions(res?.data);
        if (!rows.length) {
          message.info("Nothing from this vendor expires by that date.");
          return;
        }
        onStart({
          vendor,
          vendorName,
          rows,
          source: mode,
          reason: `Near expiry (by ${expiresBy.format("DD MMM YYYY")})`,
        });
        close();
      })
      .catch((err) => message.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      title="New Purchase Return"
      width={620}
      destroyOnClose
      footer={[
        <UiButton key="cancel" variant="secondary" onClick={close}>
          Cancel
        </UiButton>,
        <UiButton key="continue" loading={loading} onClick={handleContinue}>
          Continue
        </UiButton>,
      ]}
    >
      <Typography.Text type="secondary">
        How do you want to build this return?
      </Typography.Text>

      <div style={{ marginTop: 12 }}>
        <ModeCards modes={MODES} value={mode} onChange={setMode} />
      </div>

      {mode === "manual" ? null : (
        <div
          style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}
        >
          <div>
            <Typography.Text style={{ fontSize: 13 }}>Vendor</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <AsyncSearchSelect
                fetcher={fetchVendors}
                loadOnOpen
                placeholder="Search vendor"
                value={vendor}
                onChange={(value, option) => {
                  setVendor(value);
                  setVendorName(option?.label || "");
                }}
              />
            </div>
          </div>

          <div>
            <Typography.Text style={{ fontSize: 13 }}>Expiring By</Typography.Text>
            <div style={{ marginTop: 4 }}>
              <DatePicker
                style={{ width: "100%" }}
                format="DD MMM, YYYY"
                value={expiresBy}
                onChange={setExpiresBy}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default RaisePurchaseReturnModal;
